import { Typography } from '@mui/material'

import coloursImage from './images/colours.png'
import metaDataImage from './images/meta-data.png'
import appsImage from './images/apps-editor.png'
import fieldsImage from './images/fields-list.png'
import fieldEditorImage from './images/field-editor-open.png'
import alignmentImage from './images/alignment.png'

const Section = ({ title, children }) => <>
  <Typography variant='h5'>{title}</Typography>
  {children}
</>

const HelpText = () => (
  <div className='p-5'>
    <Typography variant='h4'>Template editor</Typography>
    <p>Use the editor to design the template that every pass issued from it will use. It previews Apple lock-screen, front and back views, plus Google list-item, front and back views. Previews are representative rather than device screenshots, so always test a live design on real devices before publishing.</p>

    <Section title='Editing a preview'>
      <p>Click an editable text area, image, barcode or field in a preview to open its editor. Empty areas can still be editable, which is useful when building a new template.</p>
      <img src={fieldEditorImage} alt='Field editor opened from an editable preview area' />
      <p>The Apple strip and primary fields, and some title/header fields, can overlap. If one item is difficult to select, use the Variables panel to select and edit it directly.</p>
    </Section>

    <Section title='Highlight editable areas'>
      <p>Open Preferences from the cog in the top bar to choose one of these modes:</p>
      <ul>
        <li><strong>Hide</strong> — no editable-area outlines.</li>
        <li><strong>Show on mouseover</strong> — show an outline only while pointing at an editable area.</li>
        <li><strong>Show</strong> — show every editable-area outline.</li>
      </ul>
      <p>Use <strong>Ctrl+Alt+E</strong> on Windows/Linux or <strong>Ctrl+Option+E</strong> on macOS to cycle through the three modes while editing.</p>
    </Section>

    <Section title='Variables and field editor'>
      <p>The Variables panel lists the template data available to the pass. Use it to add, remove and organise fields, including fields that are difficult to click in the preview. The field editor controls labels, values, formatting, alignment and the Apple/Google placement supported by the selected pass type.</p>
      <img src={fieldsImage} alt='Template variable list' />
      <p>Text placement is platform-specific. For example, Apple positions one field at the left; to place a single visible field centrally or at the right, create blank companion field slots as shown by the alignment help.</p>
      <img src={alignmentImage} alt='Apple field alignment examples' />
    </Section>

    <Section title='Colours, metadata and apps'>
      <p>The right-hand editing panel has three tabs. These settings affect the template, not an individual issued pass.</p>

      <Typography variant='h6'>Colours</Typography>
      <p>Set Apple background, foreground and label colours, and the Google background colour. Apple may choose label colour for logo text in some combinations, so test ambiguous colour combinations on a physical Apple device.</p>
      <img src={coloursImage} alt='Colour editor' />

      <Typography variant='h6'>Metadata</Typography>
      <p>
      <strong>Pass grouping name</strong>: If your users are will have more than one of your passes in their wallet, you can group them together by adding a grouping name here. This is useful, if you issue event tickets and one user is likely to have several to cover a party.<br/><br/>
      <strong>Retention period after expiry</strong>: If use pass expiry then after a pass expired we will automatically remove it form Smartix. Here you define how long we wait until we do so.<br/><br/>
      <strong>Maximum notifications</strong>: You can limit the number of past notifications that are displayed on a pass. <br/><br/>
      <strong>Apple</strong>: This option simply enables or hides the sharing button. It does not prevent your pass install URL being shared and installed on multiple passes.<br/><br/>
      <strong>Google</strong>: Google has more sharing options based around the google user ID. <strong>NOTE</strong>: Once you have issued a single pass (including a test pass) you will no longer be able to change to a One User setting. So make your choice before issuing any passes.
      </p>
      <img src={metaDataImage} alt='Metadata editor' className='border rounded-full p-3'/>

      <Typography variant='h6'>Apps</Typography>
      <p>Add an associated Apple or Google app when you want it displayed from the pass. Search for the app in the relevant store and select the matching result.</p>
      <img src={appsImage} alt='Apps editor' />
    </Section>

    <Section title='Auto-generated content'>
      <p>Apple and Google create some content that Studio cannot edit directly. Examples include Apple’s miniature pass preview on the back of a pass, system UI and wallet controls, and Google sharing/device settings. Use the <strong>Show auto-generated content</strong> preference to show or hide this reference content while you work; it does not alter the pass data.</p>
    </Section>

    <Section title='Saving and testing'>
      <p>Changes are saved to the template and may require existing passes to be synchronised before they receive the updated design. Issue test passes first, verify Apple and Google results, then use synchronisation for existing live passes where appropriate.</p>
    </Section>
  </div>
)

export default HelpText
